import { useCallback, useRef } from 'react';

const useEnhancedScroll = () => {
  const scrollTimeoutRef = useRef(null);
  const lastScrollAttemptRef = useRef(null);
  
  const scrollToVerse = useCallback((
    verseIndex,
    {
      scrollContainerRef,
      verseRefs,
      onSuccess,
      onError,
      immediate = false
    }
  ) => {
    // Clear any pending scroll attempts
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Generate unique ID for this scroll attempt
    const scrollAttemptId = Date.now();
    lastScrollAttemptRef.current = scrollAttemptId;

    const attemptScroll = (retryCount = 0, maxRetries = 15) => {
      // Check if this is still the most recent scroll attempt
      if (lastScrollAttemptRef.current !== scrollAttemptId) {
        return;
      }

      const verse = verseRefs.current[verseIndex];
      const container = scrollContainerRef.current;

      if (!verse || !container) {
        if (retryCount < maxRetries) {
          scrollTimeoutRef.current = setTimeout(() => {
            attemptScroll(retryCount + 1);
          }, Math.min(100 * (retryCount + 1), 1000)); // Cap max delay at 1s
          return;
        }
        
        console.error(`Failed to scroll to verse ${verseIndex} after ${maxRetries} attempts`);
        onError?.();
        return;
      }

      try {
        // Scroll the verse into view
        verse.scrollIntoView({
          behavior: immediate ? 'auto' : 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        // Add header offset (e.g., 72px for mobile)
        const isMobile = window.innerWidth <= 768;
        const headerOffset = isMobile ? 72 : 0;
        if (headerOffset && container.scrollTop >= headerOffset) {
          setTimeout(() => {
            container.scrollTop = container.scrollTop - headerOffset;
            onSuccess?.();
          }, immediate ? 0 : 350);
        } else {
          onSuccess?.();
        }
      } catch (error) {
        console.error('Scroll error:', error);
        onError?.();
      }
    };

    // Start scroll attempt
    attemptScroll();

    // Return cleanup
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    scrollToVerse,
    clearScrollAttempts: () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      lastScrollAttemptRef.current = null;
    }
  };
};

export default useEnhancedScroll; 